import { ParentSize } from "@visx/responsive";
import { scaleLinear } from "@visx/scale";
import { curveBasis } from "@visx/curve";
import { Group } from "@visx/group";
import { Threshold } from "@visx/threshold";
import { cn } from "@rosette/ts/src/util/cn.js";
import { LinePath } from "@visx/shape";
import { useEffect, useMemo, useState } from "react";
import { getTimeMs } from "@rosette/ts/src/util/utils.js";
import { easeInOut, easeOut } from "framer-motion";


const defaultMargin = {
    top: 14,
    right: 14,
    bottom: 14,
    left: 0,
};


function cubicBezierInterpolation(
    dataX: number[],
    dataY: number[],
    queryX: number[],
): number[] {
    // Compute slopes at each data point using for loops.
    const slopes: number[] = [];
    for (let i = 0; i < dataY.length; i++) {
        if (i === 0) {
            slopes.push((dataY[1] - dataY[0]) / (dataX[1] - dataX[0]));
        } else if (i === dataY.length - 1) {
            slopes.push((dataY[i] - dataY[i - 1]) / (dataX[i] - dataX[i - 1]));
        } else {
            slopes.push((dataY[i + 1] - dataY[i - 1]) / (dataX[i + 1] - dataX[i - 1]));
        }
    }

    // Interpolate for each query x value using a for loop.
    const results: number[] = [];
    for (let j = 0; j < queryX.length; j++) {
        const xVal = queryX[j];

        // Clamp x if it's outside the data range.
        if (xVal <= dataX[0]) {
            results.push(dataY[0]);
            continue;
        }
        if (xVal >= dataX[dataX.length - 1]) {
            results.push(dataY[dataY.length - 1]);
            continue;
        }

        // Locate the segment [dataX[i], dataX[i+1]] that contains xVal.
        let i = 0;
        while (i < dataX.length - 1 && xVal > dataX[i + 1]) {
            i++;
        }

        const x0 = dataX[i];
        const x1 = dataX[i + 1];
        const y0 = dataY[i];
        const y1 = dataY[i + 1];
        const dx = x1 - x0;

        // Calculate control points for y using the slopes.
        const cp1_y = y0 + slopes[i] * (dx / 3);
        const cp2_y = y1 - slopes[i + 1] * (dx / 3);

        // Compute the normalized parameter t.
        const t = (xVal - x0) / dx;
        const oneMinusT = 1 - t;

        // Evaluate the cubic Bézier formula.
        const bezierY = (
            oneMinusT * oneMinusT * oneMinusT * y0
            + 3 * oneMinusT * oneMinusT * t * cp1_y
            + 3 * oneMinusT * t * t * cp2_y
            + t * t * t * y1
        );

        results.push(bezierY);
    }

    return results;
}


interface AnimatedLineProps {
    percentages: number[];
    xMax: number;
    yMax: number;
    indexScale: ReturnType<typeof scaleLinear<number>>;
    percentageScale: ReturnType<typeof scaleLinear<number>>;
    animTimeMs?: number;
}


function AnimatedLine({
    percentages, xMax, yMax, indexScale, percentageScale, animTimeMs = 3000,
}: AnimatedLineProps) {

    // Data prep.
    const percentageCount = percentages.length;
    const dataX = useMemo(() => {
        const xs: number[] = [];
        for (let index = 0; index < percentageCount; ++index) {
            xs.push(indexScale(index));
        }
        return xs;
    }, [percentageCount, indexScale]);

    const dataY = useMemo(() => {
        const ys: number[] = [];
        for (const percentage of percentages) {
            ys.push(percentageScale(percentage));
        }
        return ys;
    }, [percentages, percentageScale]);

    // Curve.
     const x = useMemo(() => {
         const numPoints = xMax / 2;
         const xs: number[] = [0];
         for (let index = 1; index < numPoints - 1; ++index) {
             const t = easeInOut(index / numPoints);
             xs.push(xMax * t);
         }
         xs.push(xMax);
         return xs;
     }, [xMax]);
     const y = useMemo(() => cubicBezierInterpolation(dataX, dataY, x), [dataX, dataY, x]);

    // Animation.
    const [progress, setProgress] = useState(0);
    useEffect(() => {
        const timer: { handle: number | null } = { handle: null };
        const start = getTimeMs();
        const update = () => {
            const progress = (getTimeMs() - start) / animTimeMs;
            setProgress(Math.min(1.0, progress));
            if (progress < 1.0) {
                timer.handle = requestAnimationFrame(update);
            } else {
                timer.handle = null;
            }
        };
        update();
        return () => {
            if (timer.handle !== null) {
                cancelAnimationFrame(timer.handle);
            }
        };
    }, [animTimeMs, setProgress]);

    const graphProgress = easeInOut(Math.min(1.0, progress / 0.9));
    const iconProgress = easeOut(Math.max(0.0, (progress - 0.9) / 0.1));

    const endX = graphProgress * xMax;
    const animX = [x[0]];
    const animY = [y[0]];
    for (let index = 1; index < x.length; ++index) {
        const xVal = x[index];
        if (xVal > endX)
            break;

        animX.push(xVal);
        animY.push(y[index]);
    }
    const iconOpacity = easeInOut(Math.max(0, iconProgress));

    return (
        <>
            <Threshold<number>
                id={`${Math.random()}`}
                data={animX}
                x={(_, index) => animX[index]}
                y0={() => percentageScale(50.0)}
                y1={(_, index) => animY[index]}
                curve={curveBasis}
                clipAboveTo={0}
                clipBelowTo={yMax}
                belowAreaProps={{
                    className: "fill-ruleset-masters",
                }}
                aboveAreaProps={{
                    className: "fill-primary-strong",
                }} />

            <LinePath
                data={animX}
                x={(_, index) => animX[index]}
                y={(_, index) => animY[index]}
                curve={curveBasis}
                className="stroke-ruleset-masters"
                strokeWidth={2} />

            {/* Embed the best move analysis icon. */}
            {iconOpacity > 0 && (
                <Group
                    transform={`translate(${x[x.length - 1] - 12}, ${y[y.length - 1] - 12})`}>

                    <circle
                        cx="12"
                        cy="12"
                        r="11"
                        className="fill-border-blue"
                        opacity={iconOpacity} />

                    <circle
                        cx="12"
                        cy="12"
                        r="8"
                        fill="#f5fbff"
                        opacity={iconOpacity} />

                    <path
                        d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2m3.23 15.39L12 15.45l-3.22 1.94a.502.502 0 0 1-.75-.54l.85-3.66-2.83-2.45a.505.505 0 0 1 .29-.88l3.74-.32 1.46-3.45c.17-.41.75-.41.92 0l1.46 3.44 3.74.32a.5.5 0 0 1 .28.88l-2.83 2.45.85 3.67c.1.43-.36.77-.74.54"
                        style={{
                            fill: "#0679d1",
                            opacity: iconOpacity,
                        }} />
                </Group>
            )}
        </>
    );
}


interface InternalAnalysisSnakeGraphProps {
    className?: string;
    width: number;
    height: number;
    percentages: number[];
    margin?: { top: number; right: number; bottom: number; left: number };
}


function InternalAnalysisSnakeGraph({
    className, width, height, percentages, margin = defaultMargin,
}: InternalAnalysisSnakeGraphProps) {
    if (width < 10)
        return null;

    const indexScale = scaleLinear<number>({
        domain: [0, percentages.length - 1],
    });
    const percentageScale = scaleLinear<number>({
        domain: [
            Math.min(...percentages),
            Math.max(...percentages),
        ],
    });

    const xMax = width - margin.left - margin.right;
    const yMax = height - margin.top - margin.bottom;

    indexScale.range([0, xMax]);
    percentageScale.range([yMax, 0]);

    return (
        <svg className={className} width={width} height={height}>
            <Group left={margin.left} top={margin.top}>
                <AnimatedLine
                    percentages={percentages}
                    xMax={xMax}
                    yMax={yMax}
                    indexScale={indexScale}
                    percentageScale={percentageScale} />
            </Group>
        </svg>
    );
}


interface AnalysisSnakeGraphProps {
    className?: string;
    graphClassName?: string;
    percentages: number[];
}


export function AnalysisSnakeGraph({
    className, graphClassName, percentages,
}: AnalysisSnakeGraphProps) {
    return (
        <figure
            className={cn(
                "m-0",
                className ?? null,
            )}
            title="Light's chance of winning throughout the game">

            <div className="w-full h-24 md:h-32 select-none">
                <ParentSize>
                    {({ width, height }) => (
                        <InternalAnalysisSnakeGraph
                            className={graphClassName}
                            width={width}
                            height={height}
                            percentages={percentages} />
                    )}
                </ParentSize>
            </div>
        </figure>
    );
}
